import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, X, ChevronRight, Phone, Mail, MapPin, 
  BarChart3, Globe, ShieldCheck, Award, Briefcase, 
  HeartHandshake, Zap, Target, Users, CheckCircle2, 
  Info, Heart, UploadCloud, FileCheck, Map, Cpu, Languages, Calendar, Activity, Layers, Code, Sparkles, Database, Smile, BriefcaseBusiness, PhoneCall, ArrowRight
} from 'lucide-react';

// --- SITE-WIDE ABSTRACT BACKGROUND ---
const SiteBackground = () => (
  <div className="fixed inset-0 overflow-hidden pointer-events-none z-[-1] bg-slate-50/80">
    <div className="absolute top-[10%] left-[-10%] w-[50vw] h-[50vw] bg-[#2E3192]/[0.03] rounded-full mix-blend-multiply filter blur-[120px] animate-float-slow"></div>
    <div className="absolute top-[40%] right-[-10%] w-[45vw] h-[45vw] bg-[#00A651]/[0.03] rounded-full mix-blend-multiply filter blur-[120px] animate-float-medium" style={{animationDelay: '1s'}}></div>
  </div>
);

// --- ABSTRACT DATA ART (FOR HERO) ---
const AbstractDataArt = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 flex items-center justify-center opacity-[0.5]">
    <svg viewBox="0 0 1000 500" className="w-full h-full min-w-[1200px] object-cover" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M-100,400 L250,250 L400,350 L600,150 L800,280 L1100,100" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 4" opacity="0.8" />
      <path d="M-100,450 L200,350 L400,450 L550,250 L800,380 L1100,200" stroke="#94a3b8" strokeWidth="1" opacity="0.4" />
      <path d="M150,500 L300,100 L500,250 L700,50 L900,450" stroke="#e2e8f0" strokeWidth="2" opacity="0.6" />
      <circle cx="250" cy="250" r="4" fill="#2E3192" className="animate-pulse" />
      <circle cx="400" cy="350" r="4" fill="#E31837" />
      <circle cx="600" cy="150" r="5" fill="#00A651" className="animate-pulse" />
      <circle cx="800" cy="280" r="4" fill="#2E3192" />
      <circle cx="300" cy="100" r="3" fill="#00A651" />
      <circle cx="700" cy="50" r="3" fill="#E31837" />
    </svg>
  </div>
);

// --- ADVANCED FUTURISTIC ANIMATION COMPONENT ---
const Reveal = ({ children, delay = 0, direction = 'up', className = '', type = 'fade' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const getTransform = () => {
    if (isVisible) return 'translate-x-0 translate-y-0 scale-100 blur-none opacity-100';
    if (type === 'zoom') return 'scale-[0.98] blur-sm opacity-0';
    switch (direction) {
      case 'up': return 'translate-y-8 blur-sm opacity-0';
      case 'down': return '-translate-y-8 blur-sm opacity-0';
      case 'left': return 'translate-x-8 blur-sm opacity-0';
      case 'right': return '-translate-x-8 blur-sm opacity-0';
      default: return 'translate-y-8 blur-sm opacity-0';
    }
  };

  return (
    <div 
      ref={ref} 
      className={`transition-all duration-[800ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${getTransform()} ${className}`} 
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// --- REUSABLE SECTION HEADER ---
const SectionHeader = ({ icon, title, colorClass, bgClass = 'bg-white' }) => (
  <div className="flex items-center mb-6 gap-4">
    <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl ${bgClass} flex items-center justify-center flex-shrink-0 border border-slate-200/60 shadow-sm`}>
      {React.cloneElement(icon, { className: `w-6 h-6 md:w-8 md:h-8 ${colorClass}` })}
    </div>
    <h2 className="text-xl md:text-2xl lg:text-3xl font-black text-slate-900 uppercase tracking-tight leading-tight">{title}</h2>
  </div>
);

// --- DATA EXTRACTED EXACTLY FROM LATEST WORD DOCUMENT ---

const KEY_STATS = [
  { text: "Led by Market Research Professionals, with past-experience in National Level Leadership roles", icon: <Award />, colorClass: "text-[#E31837]", accent: "border-[#E31837]", bgClass: "bg-red-50/80" },
  { text: "The Modulus leadership team and SBU heads come with over 500+ years of combined team experience", icon: <Users />, colorClass: "text-[#00A651]", accent: "border-[#00A651]", bgClass: "bg-green-50/80" },
  { text: "6000+ varied projects delivered since 2018, encompassing almost all research techniques", icon: <CheckCircle2 />, colorClass: "text-[#2E3192]", accent: "border-[#2E3192]", bgClass: "bg-blue-50/80" },
  { text: "In-house data collection with 16 regional offices across India, and staffed by industry-reputed professionals, employing 2500+ experienced interviewers", icon: <MapPin />, colorClass: "text-[#E31837]", accent: "border-[#E31837]", bgClass: "bg-red-50/80" },
  { text: "Dedicated client servicing teams, along with superior delivery, have ensured high retention rates among the clients", icon: <HeartHandshake />, colorClass: "text-[#00A651]", accent: "border-[#00A651]", bgClass: "bg-green-50/80" },
  { text: "Customised solutions to match your needs", icon: <Target />, colorClass: "text-[#4D4DFF]", accent: "border-[#4D4DFF]", bgClass: "bg-blue-50/80" }
];

const LEADERSHIP = [
  { name: 'Tilak Choudhury', title: 'Chairperson', image: '/Tilak_Choudhury.jpg', bio: 'With over four decades in the Indian market research industry, Tilak is one of the original members of "Mode". He is widely credited with building industry-renowned field divisions, fostering a strong organisational culture, and is a passionate proponent of strong “people-first” values.' },
  { name: 'Pronab Mazumder', title: 'Managing Director', image: '/Pronab_Mazumder.jpg', bio: 'Pronab began his research career in 1980. Known for building highly effective field teams at large scales, he brings exceptional resourcing, creative problem-solving and people management skills. Currently, as the Managing Director, Pronab has grown Modulus into a respectable agency in India.' },
  { name: 'Irulappan Kannan', title: 'Director, National Field Head', image: '/Irulappan_Kannan.jpg', bio: 'With nearly three decades in operations, Kannan is recognised for establishing ISO-compliant processes. At Modulus, he leads initiatives to deliver high-quality results with speed and efficiency as National Field Head.' },
  { name: 'Sandip Bhattacharya', title: 'Director, National Head – Scripting & Data Processing', image: '/Sandip_Bhattacharya.jpg', bio: 'With almost three decades of experience in analysis and having led the Global Data Processing Unit at GfK Mode, Sandip brings deep expertise in analytical tools and global team servicing. A detail-oriented leader, he heads our scripting and data processing functions.' },
  { name: 'Debaprabho Bhattacharya', title: 'Director, National Head - Research', image: '/Debaprabho_Bhattacharya.jpg', bio: 'With over 25 years of experience as a researcher, Debo is skilled in a wide range of research tools and techniques across multiple sectors and has held leadership roles in previous organisations. He leads our team of researchers and is known for delivering strong insights to clients.' },
  { name: 'Amar Kumar Singh', title: 'Director, Client Servicing', image: '/Amar_Kumar_Singh.jpg', bio: 'Amar was inducted as a Director in 2025. Amar brings more than 25 years of market research experience, blending operations management with client servicing. With more than two decades spent serving premium clients, he is dedicated to delivering excellence in every engagement.' }
];

const JOBS = [
  {
    title: 'Data Processing Specialist', level: 'A', division: 'Data Processing', location: 'Kolkata, Chennai',
    brief: 'The Data Processing Specialist helps internal clients define data processing needs aligned with client objectives. This role involves assisting with project estimates, tabulation plans, banner plans, verbatim coding frameworks, timelines, and field resources. The specialist works closely with Project Managers and Delivery Managers to ensure data integrity and coordinates with vendors on back-end deliverables.',
    duties: [
      'Collaborate with Project and Delivery Managers to understand client objectives and ensure expectations are met',
      'Verify data accuracy through logic checks and post-field validation',
      'Create tabulation programs, tables, and other outputs based on analysis specifications',
      'Serve as a technical expert in data processing',
      'Work with Survey Programmers to embed efficiencies in online collection',
      'Assist in fieldwork management and vendor coordination',
      'Design tab and banner plans to meet client needs',
      'Review data, produce top-line summaries, and support analytical outcomes'
    ],
    skills: ['Practical experience with quantitative research methods', 'Strong academic foundation in research techniques', 'Proficiency in Quantum, SPSS, SAS, Merlin, Excel', 'Working knowledge of SQL+ and MS Access'],
    attributes: ['Analytical thinking', 'Clear communication under pressure', 'Attention to detail', 'Sincerity and result orientation'],
    interactions: ['Internal: Research and PMT teams', 'External: Clients']
  },
  {
    title: 'Research', level: 'A', division: 'Research', location: 'Delhi',
    brief: 'Key responsibilities span Research Management, Financial Control & Monitoring, and Client Management.',
    duties: [
      'RESEARCH MANAGEMENT',
      'Support supervisors in brainstorming, secondary research, and client meetings',
      'Assist in proposal drafting, formatting, and quality control',
      'Help create questionnaires, field kits, and coordinate fieldwork logistics',
      'Monitor fieldwork progress and quality',
      'Assist in developing analysis plans, reading tables, and deriving insights',
      'Create presentation templates and populate data with accuracy',
      'Handle basic slide-level conclusions',
      'Manage project timelines and logistics',
      'FINANCIAL CONTROL & MONITORING',
      'Assist in building robust project budgets',
      'Monitor field costs and identify efficiencies',
      'Support timely invoicing and follow-ups',
      'CLIENT MANAGEMENT',
      'Support client understanding and relationship management',
      'Contribute to business generation efforts'
    ],
    skills: ['Postgraduate degree, preferably in social sciences', 'Working knowledge of MS Excel and PowerPoint'],
    attributes: ['Analytical thinking', 'Clear communication under pressure', 'Attention to detail', 'Sincerity and result orientation'],
    interactions: ['Internal: Analysis and PMT teams', 'External: Clients']
  },
  {
    title: 'Field Executive', level: 'Executive', division: 'Field Operations', location: 'Pan India (Travel Required)',
    brief: 'We are looking for an experienced field executive with strategic vision to join our growing team. In this role, you will manage various market research activities and coordinate closely with the research team.',
    duties: [
      'Coordinate with the research team to understand project objectives and requirements',
      'Lead team briefings and training',
      'Ensure timely, high-quality project completion',
      'Validate questionnaires and translations'
    ],
    skills: [
      'Bachelor’s degree or higher from a recognised university', 
      'Age below 25 years', 
      'Willingness to travel outstation', 
      'Proficiency in Hindi, English, and the relevant regional language', 
      'Willingness to work in other states as needed', 
      'Strong written and verbal communication skills'
    ],
    attributes: [],
    interactions: []
  }
];

const OFFICES = [
  { city: 'Kolkata', type: 'Registered & Corporate Office', address: 'Modulus Research & Analysis Pvt. Ltd. 44, Shantipally, Sanjay Bhawan, 5th Floor Kolkata-700107 | India Tel: +91 (33) 4603 1765', image: '/Kolkata.jpg' },
  { city: 'Delhi', type: 'Regional Office', address: '15, Community Centre Sandhya Deep Building, 1st & 3rd Floor East of Kailash, Behind Bank of Baroda New Delhi – 110065', image: '/Delhi.jpg' },
  { city: 'Mumbai', type: 'Regional Office', address: '1608/132 Tirthankar Estate, Aarey Road, Gogate Wadi, Ganesh Dham, Goregaon East, Mumbai – 400063', image: '/Mumbai.jpg' },
  { city: 'Chennai', type: 'Regional Office', address: 'No – 42 (Old no. 11), Trustpuram 5th Cross Street, Kodambakkam Chennai – 600024', image: '/Chennai.jpg' },
  { city: 'Bengaluru', type: 'Regional Office', address: 'No 49/1, 2nd Floor, 11th Cross, 8th Main, Wilson Garden, Bangalore – 560027', image: '/Bengaluru.jpg' },
  { city: 'Lucknow', type: 'Regional Office', address: 'A4/5, Corporation Flats Nirala Nagar, Near Vivekanand Railway Crossing Lucknow – 226020', image: '/Lucknow.jpg' },
  { city: 'Ludhiana', type: 'Regional Office', address: 'H No. 1086/1, St No.5/4, Harcharan Nagar, Samarala Chowk, Opp. Nanaksar Gurudwara, C/o Rajput Motor, Ludhiana – 141008', image: '/Ludhiana.jpg' },
  { city: 'Ahmedabad', type: 'Regional Office', address: '915, Span Trade Centre 9th Floor, Opp. Kocharab Ashram, Paldi Ahmedabad – 380007', image: '/Ahmedabad.jpg' },
  { city: 'Nagpur', type: 'Regional Office', address: 'Mangaldeep Apartment, Flat No. N-1, Plot No. 13 & 14, Farmland Ramdaspeth, Near Gurudwara, Nagpur – 440010', image: '/Nagpur.jpg' },
  { city: 'Patna', type: 'Regional Office', address: 'Plot No. A-170, Sita Sadan, 3rd Floor, People’s Cooperative Colony Kankarbagh, Beside Modern Hospital Patna – 800020', image: '/Patna.jpg' },
  { city: 'Bhubaneswar', type: 'Regional Office', address: 'Plot No-1616 (216/12), Ramesh Niwas, Ground Floor, Lane-9, Aerodrome Area, Bhubaneswar – 751020', image: '/Bhubaneswar.jpg' },
  { city: 'Hyderabad', type: 'Regional Office', address: 'No. 1-8-168/A, 4th Floor Aurora’s College Lane, Chikkadpally Hyderabad – 500020', image: '/Hyderabad.jpg' },
  { city: 'Madurai', type: 'Regional Office', address: '10/4, Second Floor, Ponni Nagar, By-Pass Road, Near Hotel Jeyasakthi Madurai - 625016', image: '/Madurai.jpg' },
  { city: 'Pune', type: 'Regional Office', address: 'Flat No. 15, 5th floor, Radha Krishna Heights, Bhopatkar Road, Khajina Vihir, Sadashiv Peth, Pune – 411030', image: '/Pune.jpg' },
  { city: 'Guwahati', type: 'Regional Office', address: 'Modulus Research & Analysis Pvt. Ltd. H/N- 263A,1st Floor | Chitraban Path, R.G. Baruah. Road. Guwahati-781003 | India', image: '/Guwahati.jpg' },
  { city: 'Kochi', type: 'Regional Office', address: 'No,70/1761, ABMS lane, Ashoka Road (Behind Welmart Supermarket), Kaloor, Kochi – 682017', image: '/Kochi.jpg' }
];

const INDUSTRIES = [
  "FMCG & Consumer Goods", "Automotive", "Technology & Telecom", 
  "Financial Services", "Healthcare & Pharmaceuticals", "Retail", "Media & Entertainment"
];

// --- PREMIUM LIGHT THEME TOKENS ---
const theme = {
  bg: 'bg-transparent',
  bentoCard: 'bg-white/80 backdrop-blur-xl border border-white/60 shadow-lg rounded-2xl hover:border-white/80 hover:shadow-xl transition-all duration-300',
  gradientText: 'text-transparent bg-clip-text bg-gradient-to-r from-[#2E3192] via-[#E31837] to-[#00A651]',
  buttonPrimary: 'bg-[#2E3192] text-white hover:bg-[#1a1c5b] transition-all duration-300 font-bold shadow-md',
  buttonSecondary: 'bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-800 transition-all duration-300 font-bold shadow-sm'
};

// --- PAGE COMPONENTS ---

const HomePage = ({ navigateTo }) => (
  <div className="overflow-hidden pb-4 md:pb-8">
    <div className={`relative min-h-[60vh] md:min-h-[65vh] flex items-center justify-center overflow-hidden py-8 md:py-10`}>
      
      <AbstractDataArt />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 relative z-10 w-full flex flex-col items-center text-center">
        
        {/* Wrapper to shrink-to-fit the H1 width so the badge aligns with 'M' */}
        <div className="inline-flex flex-col items-start text-center">
          <Reveal direction="up" delay={100} className="text-left w-full">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/80 backdrop-blur-md border border-slate-200/80 text-slate-800 text-[11px] md:text-xs font-black tracking-widest uppercase mb-4 shadow-sm">
              <Sparkles className="w-4 h-4 mr-2 text-[#00A651]" />
              Data Integrity & Speed
            </div>
          </Reveal>
          
          <Reveal direction="up" delay={200} className="w-full">
            <h1 className="text-3xl md:text-4xl lg:text-[52px] font-black tracking-tight mb-3 text-[#0a1f44] max-w-4xl drop-shadow-sm pb-1 text-center" style={{ lineHeight: '1.2' }}>
              An experienced, dependable <br/>
              <span className={theme.gradientText}>
                Market Research Partner
              </span> <br/>
              for your critical decisions
            </h1>
          </Reveal>
        </div>

        <Reveal direction="up" delay={300} className="w-full">
          <p className="text-sm md:text-base font-bold text-[#2E3192] mt-3 tracking-widest uppercase flex items-center justify-center">
            REAL CONSUMER INSIGHTS, FROM REAL CONSUMER DATA.
          </p>
        </Reveal>

        <Reveal direction="up" delay={400} className="w-full">
          <p className="text-sm md:text-lg text-slate-600 max-w-2xl mt-4 mb-6 leading-relaxed font-medium mx-auto text-center">
            Empowering brands with uncompromising primary research data, delivered with speed and integrity across India and beyond.
          </p>
        </Reveal>

        <Reveal direction="up" delay={500} className="w-full">
          <div className="flex flex-col sm:flex-row gap-4 mt-0 justify-center">
            <button onClick={() => navigateTo('Contact')} className={`group px-8 py-3 rounded-xl text-sm md:text-base ${theme.buttonPrimary} flex items-center justify-center`}>
              Discuss your research needs
              <ArrowRight className="ml-3 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigateTo('Services')} className={`px-8 py-3 rounded-xl text-sm md:text-base ${theme.buttonSecondary} flex items-center justify-center`}>
              Explore Our Services
            </button>
          </div>
        </Reveal>
      </div>
    </div>

    {/* Key Stats - Light Bento Grid */}
    <div className="relative z-20 pb-16 pt-8">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch">
          {KEY_STATS.map((stat, idx) => (
            <Reveal key={idx} delay={idx * 100} direction="up" className="h-full">
              <div className={`flex flex-col items-start p-8 ${theme.bentoCard} h-full group border-l-4 ${stat.accent}`}>
                <div className={`w-14 h-14 flex items-center justify-center rounded-xl mb-6 ${stat.bgClass} border border-slate-200/50 shadow-sm group-hover:scale-105 transition-transform duration-500`}>
                  {React.cloneElement(stat.icon, { className: `w-7 h-7 ${stat.colorClass}` })}
                </div>
                <p className="text-slate-800 text-base font-medium leading-relaxed">{stat.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>

    {/* Our Three Core Pillars */}
    <div className="py-16 relative bg-white/70 backdrop-blur-md border-t border-white/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Our Core Pillars</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#E31837] via-[#00A651] to-[#2E3192] rounded-full mb-6"></div>
            <p className="text-base md:text-lg text-slate-700 font-medium">The foundation of everything we do at Modulus Research & Analysis.</p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            { icon: <ShieldCheck />, title: "High-Quality Data", desc: "We are widely recognised for delivering the highest quality primary research data in India. Over the years, we have learned that process and technology alone are not enough - company culture is just as essential to capturing reliable data.", colorClass: "text-[#E31837]", bgClass: "bg-red-50/80" },
            { icon: <Zap />, title: "High Speed of Delivery", desc: "For information to be relevant, it must be delivered on time. At Modulus, we combine meticulous planning, real-time monitoring, dynamic resourcing, and dedicated effort to ensure every project meets its deadline.", colorClass: "text-[#00A651]", bgClass: "bg-green-50/80" },
            { icon: <Smile />, title: "Happy & Uplifting Work Culture", desc: "We believe that if our team members dread coming to work, we have a serious problem. We take pride in being a people-first organisation - because only a positive team can create genuine client delight.", colorClass: "text-[#2E3192]", bgClass: "bg-blue-50/80" }
          ].map((pillar, idx) => (
            <Reveal key={idx} delay={idx * 150} direction="up" className="h-full">
              <div className={`p-8 ${theme.bentoCard} flex flex-col h-full`}>
                <SectionHeader icon={pillar.icon} title={pillar.title} colorClass={pillar.colorClass} bgClass={pillar.bgClass} />
                <p className="text-slate-700 leading-relaxed font-medium text-base">{pillar.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>

    {/* Why Choose Us */}
    <div className="py-16 relative bg-slate-50/70 backdrop-blur-md border-t border-white/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className={`p-10 md:p-12 bg-white border border-slate-200 shadow-sm rounded-2xl flex flex-col lg:flex-row items-center gap-12 overflow-hidden relative border-l-4 border-l-[#00A651]`}>
            <div className="lg:w-1/2 relative z-10 w-full">
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-8 tracking-tight">Why Clients Choose Modulus</h2>
              <ul className="space-y-6">
                {[
                  "Uncompromising quality in primary research data",
                  "Reliable on-time delivery",
                  "A positive, people-first culture that reflects in client service",
                  "Direct involvement of senior team members in every project"
                ].map((item, i) => (
                  <li key={i} className="flex items-start group">
                    <div className="bg-green-50/80 p-2 md:p-3 rounded-xl mr-4 border border-green-200/50 shadow-sm mt-0.5 flex-shrink-0 group-hover:bg-[#00A651]/10 transition-colors">
                      <CheckCircle2 className="w-5 h-5 md:w-6 md:h-6 text-[#00A651]" />
                    </div>
                    <span className="text-base md:text-lg text-slate-800 font-semibold leading-relaxed group-hover:text-slate-900 transition-colors">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:w-1/2 w-full h-[300px] md:h-[400px] relative rounded-2xl overflow-hidden shadow-md border border-white/50 mt-8 lg:mt-0">
               <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop" alt="Team" className="w-full h-full object-cover absolute inset-0 transition-transform duration-[2000ms] hover:scale-105" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </div>
);

const AboutPage = () => (
  <div className="pb-12 pt-8 md:pt-10 border-t border-white/40 bg-white/70 backdrop-blur-md">
    <div className="py-8 md:py-10 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
        <Reveal><h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight text-slate-900">About <span className={theme.gradientText}>Us</span></h1><p className="text-base md:text-xl text-slate-700 max-w-3xl mx-auto font-medium">A heritage of excellence, driven by a modern vision.</p></Reveal>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-12">
      <Reveal>
        <div className={`mb-16 p-8 md:p-12 ${theme.bentoCard} relative overflow-hidden border-t-4 border-[#E31837]`}>
          <SectionHeader icon={<Target />} title="Our Story" colorClass="text-[#E31837]" bgClass="bg-red-50/80" />
          <div className="text-slate-700 font-medium leading-relaxed text-base space-y-6 relative z-10">
            <p>Modulus Research & Analysis Pvt. Ltd. was founded on <strong className="font-bold text-slate-900">April 6, 2018</strong>, with a clear purpose: to deliver high-quality primary research data with integrity and speed.</p>
            <p>Our roots, however, go much further back. Our leadership team has been part of the Indian research industry since 1981, with deep experience at renowned organisations such as Kantar, Nielsen and GfK. While the Modulus people have been part of earlier mergers (TNS and GfK), 2017-18 offered a unique opportunity to start an Indian MR company. Our passion for data-integrity and consumer-understanding blended seamlessly with the added capabilities in global processes, operations, research techniques, and data processing.</p>
            <div className="mt-8 p-6 md:p-8 rounded-2xl bg-blue-50/80 border border-blue-200/50 flex flex-col sm:flex-row items-start sm:items-center shadow-sm relative overflow-hidden">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white rounded-xl flex items-center justify-center mr-0 sm:mr-6 flex-shrink-0 shadow-sm mb-4 sm:mb-0 border border-slate-100">
                <Target className="w-6 h-6 md:w-8 md:h-8 text-[#2E3192]" />
              </div>
              <p className="text-base md:text-lg font-semibold text-slate-800 m-0 leading-relaxed relative z-10">Today, we are <strong className="text-[#2E3192] font-bold">8 years old</strong> and have successfully delivered over <strong className="text-[#2E3192] font-bold">6,000+ projects</strong>, partnering with some of the most respected brands in India and around the world. Modulus provides a unique ecosystem for passionate market research professionals to develop their skills and, alongside, deliver insights to clients.</p>
            </div>
          </div>
        </div>
      </Reveal>

      {/* Core Management Team */}
      <div className="mb-16">
        <Reveal>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Core Management Team</h2>
            <p className="text-base md:text-lg text-slate-700 font-medium max-w-3xl mx-auto leading-relaxed px-4">The five Founder-Directors who comprise the core team bring a wide range of experience and skills to the company.</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {LEADERSHIP.slice(0, 5).map((leader, idx) => (
            <Reveal key={idx} delay={idx * 100} direction="up" type="zoom" className="h-full">
              <div className={`${theme.bentoCard} overflow-hidden group flex flex-col h-full text-center relative border-b-4 border-transparent hover:border-[#2E3192]`}>
                <div className="pt-8 pb-4 flex justify-center bg-slate-50 relative z-10 border-b border-slate-100">
                  <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-white shadow-md group-hover:scale-105 transition-transform duration-500 bg-white">
                    <img 
                      src={leader.image} 
                      alt={leader.name} 
                      className="w-full h-full object-cover object-top" 
                      onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${leader.name.replace(' ', '+')}&background=f1f5f9&color=0f172a&size=256`; }}
                    />
                  </div>
                </div>
                <div className="p-6 md:p-8 pt-0 flex flex-col flex-grow z-10 bg-white">
                  <p className="text-[#E31837] font-bold text-xs uppercase tracking-widest mb-2 mt-4">{leader.title}</p>
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-3">{leader.name}</h3>
                  <p className="text-slate-700 text-sm font-medium leading-relaxed flex-grow">{leader.bio}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Extended Leadership Team */}
      <div className="mb-16">
        <Reveal>
          <div className="text-center mb-12 mt-12 border-t border-slate-200/50 pt-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Extended Leadership Team</h2>
            <p className="text-base md:text-lg text-slate-700 font-medium max-w-3xl mx-auto leading-relaxed px-4">Modulus has grown manyfold since its inception. The Core Management Team believes in recognising individuals who not only help build Modulus but also subscribe to its “people first” ethos.</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-center">
          {LEADERSHIP.slice(5).map((leader, idx) => (
            <Reveal key={idx} delay={idx * 100} direction="up" type="zoom" className="h-full sm:col-start-1 lg:col-start-2">
              <div className={`${theme.bentoCard} overflow-hidden group flex flex-col h-full text-center relative border-b-4 border-transparent hover:border-[#00A651]`}>
                <div className="pt-8 pb-4 flex justify-center bg-slate-50 relative z-10 border-b border-slate-100">
                  <div className="w-28 h-28 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-white shadow-md group-hover:scale-105 transition-transform duration-500 bg-white">
                    <img 
                      src={leader.image} 
                      alt={leader.name} 
                      className="w-full h-full object-cover object-top" 
                      onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${leader.name.replace(' ', '+')}&background=f1f5f9&color=0f172a&size=256`; }}
                    />
                  </div>
                </div>
                <div className="p-6 md:p-8 pt-0 flex flex-col flex-grow z-10 bg-white">
                  <p className="text-[#00A651] font-bold text-xs uppercase tracking-widest mb-2 mt-4">{leader.title}</p>
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-3">{leader.name}</h3>
                  <p className="text-slate-700 text-sm font-medium leading-relaxed flex-grow">{leader.bio}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Reveal direction="right" className="h-full">
          <div className={`p-8 md:p-12 ${theme.bentoCard} h-full relative overflow-hidden group border-t-4 border-t-[#2E3192]`}>
            <Globe className="absolute top-1/2 right-[-10%] -translate-y-1/2 w-[350px] md:w-[450px] opacity-[0.03] text-[#2E3192] pointer-events-none stroke-[1] group-hover:scale-105 transition-transform duration-1000" />
            <SectionHeader icon={<Map />} title="Our Team & Footprint" colorClass="text-[#2E3192]" bgClass="bg-blue-50/80" />
            <ul className="space-y-5 relative z-10 text-base font-semibold text-slate-800 mt-8">
              <li className="flex items-start"><Users className="w-5 h-5 md:w-6 md:h-6 mr-4 text-[#00A651] shrink-0 mt-0.5" /> <span><strong className="text-slate-900">123+</strong> team members</span></li>
              <li className="flex items-start"><Target className="w-5 h-5 md:w-6 md:h-6 mr-4 text-[#E31837] shrink-0 mt-0.5" /> <span>Experts in both quantitative and qualitative research, and adept at high-end research tools</span></li>
              <li className="flex items-start"><BriefcaseBusiness className="w-5 h-5 md:w-6 md:h-6 mr-4 text-[#2E3192] shrink-0 mt-0.5" /> <span>In-house large data collection capability with <strong className="text-slate-900">16 regional offices</strong>, each led by a dedicated field manager, a network designed to navigate India’s linguistic and cultural diversity while maintaining rigorous operational control.</span></li>
              <li className="flex items-start"><Database className="w-5 h-5 md:w-6 md:h-6 mr-4 text-[#00A651] shrink-0 mt-0.5" /> <span>Large in-house data-processing, scripting and dashboarding team</span></li>
              <li className="flex items-start"><MapPin className="w-5 h-5 md:w-6 md:h-6 mr-4 text-[#E31837] shrink-0 mt-0.5" /> <span><strong className="text-slate-900">2,500+</strong> experienced interviewers across India</span></li>
            </ul>
          </div>
        </Reveal>
        <Reveal direction="left" className="h-full">
          <div className={`p-8 md:p-12 ${theme.bentoCard} h-full relative overflow-hidden group border-t-4 border-t-[#E31837]`}>
            <HeartHandshake className="absolute top-1/2 right-[-10%] -translate-y-1/2 w-[350px] md:w-[450px] opacity-[0.03] text-[#E31837] pointer-events-none stroke-[1] group-hover:scale-105 transition-transform duration-1000" />
            <SectionHeader icon={<HeartHandshake />} title="Our Culture" colorClass="text-[#E31837]" bgClass="bg-red-50/80" />
            <div className="text-slate-700 space-y-6 text-base font-medium leading-relaxed mt-8 relative z-10">
              <p>We believe that high-quality project outcomes are driven by the involvement of senior team members - their knowledge, commitment, and accessibility benefit both their teams and our clients.</p>
              <p>We strive to build an uplifting, supportive workplace - because only positive team members can deliver outstanding work. We are committed to fairness, continuous skill development, and addressing knowledge gaps across the organisation.</p>
              <p>If our employees ever felt they were simply "showing up" to work, we would consider that a failure. We take pride in being a people-first organisation.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </div>
);

const ServicesPage = () => (
  <div className="pb-12 pt-8 md:pt-10 border-t border-white/40 bg-slate-50/70 backdrop-blur-md">
    <div className="py-8 md:py-10 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
        <Reveal><h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tight text-slate-900">Our <span className={theme.gradientText}>Services</span></h1><p className="text-base md:text-xl text-slate-700 max-w-3xl mx-auto font-medium">A single-window service backed by high-calibre consultancy.</p></Reveal>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-12 space-y-12">
      <Reveal>
        <div className={`p-8 md:p-12 ${theme.bentoCard} text-center max-w-4xl mx-auto relative overflow-hidden`}>
          <p className="text-base text-slate-700 font-medium leading-relaxed mb-6 relative z-10">Whether it involves quantitative surveys, qualitative research, mystery audits, or observational studies, we manage the entire project lifecycle—from study design and tool preparation to fieldwork, analysis, top-line discussions, and final presentations.</p>
          <p className="text-lg md:text-xl font-bold text-slate-900 mb-6 relative z-10">What you receive is a single-window service backed by high-calibre consultancy.</p>
          <p className="text-slate-700 font-medium text-sm md:text-base relative z-10">We offer a wide range of services tailored to your business needs, with proficiency across diverse methodologies and sectors.</p>
        </div>
      </Reveal>

      {/* Services Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Research (Spans 2 cols) */}
        <Reveal delay={100} className="lg:col-span-2 h-full">
          <div className={`p-8 md:p-12 ${theme.bentoCard} h-full flex flex-col relative overflow-hidden group border-t-4 border-t-[#E31837]`}>
            <Target className="absolute top-1/2 right-[-5%] -translate-y-1/2 w-[350px] opacity-[0.03] text-[#E31837] pointer-events-none stroke-[1] group-hover:scale-105 transition-transform duration-1000" />
            
            <SectionHeader icon={<Target />} title="Research" colorClass="text-[#E31837]" bgClass="bg-red-50/80" />
            <p className="text-base md:text-lg text-slate-700 font-medium mb-8 leading-relaxed relative z-10">End-to-end custom research studies are our forte. We specialise in both qualitative and quantitative research, and we are adept at handling a broad spectrum of studies. Our primary clients are the marketing, product, and sales teams of large and mid-sized companies across India.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50/80 p-6 md:p-8 rounded-2xl border border-slate-200/60 flex-grow shadow-inner relative z-10">
              <div>
                <h4 className="font-bold text-slate-900 mb-4 text-xs md:text-sm uppercase tracking-widest border-b border-slate-200/60 pb-2">Research areas include</h4>
                <ul className="space-y-3 text-slate-700 font-medium text-sm">
                  {[
                    "Usage & Attitude Studies (U&A), Market Mapping, and Consumer Segmentation and Profiling",
                    "Concept Tests, Concept Product Tests, Product Tests",
                    "Product Feature Optimisation",
                    "Pack Testing",
                    "Pricing Studies",
                    "Advertising Testing, Narrimatic Testing",
                    "Volume Estimation",
                    "Brand Health Measurement, with Brand Choice Drivers and Brand Equity",
                    "Switching / Gain-Loss Analysis",
                    "Customer Satisfaction (C-Sat)",
                    "Retail Health (Availability and Visibility Tracking)",
                    "Social Media Analytics",
                    "And more"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start"><div className="w-1.5 h-1.5 bg-[#E31837] rounded-full mr-3 mt-1.5 shadow-[0_0_5px_rgba(227,24,55,0.5)] flex-shrink-0"></div> <span className="leading-snug">{item}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-bold text-slate-900 mb-4 text-xs md:text-sm uppercase tracking-widest border-b border-slate-200/60 pb-2">Sectors we serve</h4>
                <p className="text-slate-700 font-medium leading-relaxed text-sm">FMCG (Food, Beverages, Liquor, Tobacco, Staples), Telecom, Finance, Media, Pharma, Retail, Automotive and Service Industries.</p>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Data Collection & Consumer Immersions (Stacked in 1 col) */}
        <div className="lg:col-span-1 flex flex-col gap-8">
          <Reveal delay={200} className="h-full">
            <div className={`p-8 md:p-10 ${theme.bentoCard} h-full flex flex-col relative overflow-hidden group border-t-4 border-t-[#40C4FF]`}>
              <SectionHeader icon={<Database />} title="Data Collection" colorClass="text-[#40C4FF]" bgClass="bg-blue-50/80" />
              <p className="text-slate-700 font-medium leading-relaxed text-sm md:text-base mb-6 flex-grow relative z-10">If you already have a questionnaire and analysis platform and simply need high-quality fieldwork, we are happy to help. Many clients regularly outsource their qualitative and quantitative fieldwork to us.</p>
              <div className="bg-blue-50/80 p-4 rounded-xl border border-blue-200/60 relative z-10 shadow-sm">
                <p className="text-[#2E3192] font-bold text-sm leading-relaxed">What you get is reliable data without the overhead of managing field operations in-house.</p>
              </div>
            </div>
          </Reveal>
          
          <Reveal delay={300} className="h-full">
            <div className={`p-8 md:p-10 ${theme.bentoCard} h-full flex flex-col relative overflow-hidden group border-t-4 border-t-[#00A651]`}>
              <SectionHeader icon={<Users />} title="Immersions" colorClass="text-[#00A651]" bgClass="bg-green-50/80" />
              <p className="text-slate-700 font-medium leading-relaxed text-sm md:text-base mb-6 flex-grow relative z-10">Whether you need consumer visits arranged for your senior team, a small group of consumers to test a confidential product in your office, or a consumer panel for regular feedback, we handle it all.</p>
              <div className="bg-green-50/80 p-4 rounded-xl border border-green-200/60 relative z-10 shadow-sm">
                <p className="text-[#00A651] font-bold text-sm leading-relaxed">What you gain is deep consumer understanding without the logistical complexity.</p>
              </div>
            </div>
          </Reveal>
        </div>

      </div>

      <Reveal>
        <div className={`p-8 md:p-12 ${theme.bentoCard} relative overflow-hidden border-t-4 border-[#2E3192] group`}>
          <Layers className="absolute top-1/2 right-[-5%] -translate-y-1/2 w-[350px] opacity-[0.03] text-[#2E3192] pointer-events-none stroke-[1] group-hover:scale-105 transition-transform duration-1000" />
          
          <div className="relative z-10 mb-8">
            <SectionHeader icon={<Layers />} title="Analytics and More" colorClass="text-[#2E3192]" bgClass="bg-blue-50/80" />
            <p className="text-base md:text-lg text-slate-700 font-medium leading-relaxed max-w-4xl">We offer a comprehensive suite of data processing and analytics services, all managed by our experienced in-house team. Whether you need raw data processed, advanced analysis, or interactive dashboards, we have the expertise to deliver.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 relative z-10">
            {/* Box 1 */}
            <div className="bg-slate-50/80 p-6 md:p-8 rounded-2xl border border-slate-200/60 shadow-sm h-full flex flex-col group">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center"><Cpu className="w-5 h-5 md:w-6 md:h-6 mr-3 text-[#2E3192]"/> Data Processing & Analysis / Tabulation</h3>
              <p className="text-sm text-slate-700 font-medium mb-3">We work with any data format, including:</p>
              <ul className="space-y-3 text-slate-700 font-medium text-sm md:text-base mb-6 flex-grow">
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#2E3192] rounded-full mr-2.5 flex-shrink-0"></span> ASCII (single-punch / multi-punch)</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#2E3192] rounded-full mr-2.5 flex-shrink-0"></span> Single card / multi-card / multilevel / trailer card</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#2E3192] rounded-full mr-2.5 flex-shrink-0"></span> Data checking and validation</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#2E3192] rounded-full mr-2.5 flex-shrink-0"></span> Simple to complex tabulation</li>
              </ul>
              <div className="bg-white/80 p-5 rounded-xl border border-slate-200/60 mt-auto shadow-sm">
                <p className="text-xs font-bold text-slate-900 mb-3 tracking-widest uppercase">Predictive Analysis:</p>
                <ul className="space-y-2 text-slate-700 font-medium text-sm">
                  <li>• Multivariate analysis (regression, correlation, TURF, etc.)</li>
                  <li>• Conjoint / Orthogonal / CBC / Maxdiff – design, analysis, simulation, utility scores</li>
                  <li>• Weighting (target, ratio, factor, universe, etc.)</li>
                </ul>
              </div>
            </div>

            {/* Box 2 */}
            <div className="bg-slate-50/80 p-6 md:p-8 rounded-2xl border border-slate-200/60 shadow-sm h-full flex flex-col group">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center"><Code className="w-5 h-5 md:w-6 md:h-6 mr-3 text-[#00A651]"/> Survey Scripting (CAPI / CATI / CAWI)</h3>
              <p className="text-sm text-slate-700 font-medium mb-3">Our in-house scripting capabilities include:</p>
              <ul className="space-y-4 text-slate-700 font-medium text-sm md:text-base flex-grow">
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#00A651] rounded-full mr-2.5 flex-shrink-0"></span> Complex mobile survey design</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#00A651] rounded-full mr-2.5 flex-shrink-0"></span> Integration of photo, audio, and video capture</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#00A651] rounded-full mr-2.5 flex-shrink-0"></span> Silent voice recording</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#00A651] rounded-full mr-2.5 flex-shrink-0"></span> Real-time GPS tracking of interviewers</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#00A651] rounded-full mr-2.5 flex-shrink-0"></span> Auto-flagging of suspicious interviews (duration, straight-lining)</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#00A651] rounded-full mr-2.5 flex-shrink-0"></span> Full QC console for interview acceptance or rejection</li>
              </ul>
            </div>

            {/* Box 3 */}
            <div className="bg-slate-50/80 p-6 md:p-8 rounded-2xl border border-slate-200/60 shadow-sm h-full flex flex-col group">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center"><BarChart3 className="w-5 h-5 mr-3 text-[#E31837]"/> Data Visualisation & Charting</h3>
              <p className="text-sm text-slate-700 font-medium mb-4">Our experienced charting team transforms data into clear, insightful visuals. Capabilities:</p>
              <ul className="space-y-4 text-slate-700 font-medium text-sm md:text-base flex-grow">
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#E31837] rounded-full mr-2.5 flex-shrink-0"></span> Manual and automated charting</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#E31837] rounded-full mr-2.5 flex-shrink-0"></span> Custom online dashboards with tiered user access</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#E31837] rounded-full mr-2.5 flex-shrink-0"></span> Verbatim coding from Excel/CSV</li>
                <li className="flex items-start"><span className="w-1.5 h-1.5 mt-1.5 md:mt-2 bg-[#E31837] rounded-full mr-2.5 flex-shrink-0"></span> Interactive data visualisation</li>
              </ul>
            </div>
            
            {/* Box 4 */}
            <div className="bg-slate-50/80 p-6 md:p-8 rounded-2xl border border-slate-200/60 shadow-sm h-full flex flex-col group">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center"><Languages className="w-5 h-5 mr-3 text-[#00A651]"/> Verbatim Coding & Translation</h3>
              <div className="space-y-5 text-sm md:text-base text-slate-700 font-medium leading-relaxed flex-grow mt-2">
                <p>
                  <strong className="font-bold text-slate-900 uppercase tracking-widest text-xs block mb-1">Verbatim Coding</strong> 
                  Our in-house coding team delivers consistent, high-quality verbatim coding for domestic and international clients.
                </p>
                <div className="w-full h-px bg-slate-200/60"></div>
                <p>
                  <strong className="font-bold text-slate-900 uppercase tracking-widest text-xs block mb-1">Translation</strong> 
                  Our internal translation team is proficient in all foreign and Indian languages. In addition to full-service projects, we offer standalone translation services to select international clients.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* CATI Services */}
      <Reveal>
        <div className="bg-[#0a1f44] text-white rounded-3xl p-8 md:p-12 flex flex-col md:flex-row gap-8 items-center shadow-lg relative overflow-hidden group border border-slate-800">
          <div className="md:w-2/3 relative z-10 w-full">
            <h2 className="text-2xl md:text-3xl font-black mb-5 uppercase tracking-tight flex items-center">
              <div className="p-3 md:p-4 bg-white/10 rounded-xl mr-4 border border-white/20 shadow-sm">
                 <PhoneCall className="w-6 h-6 md:w-8 md:h-8 text-[#00A651] flex-shrink-0" /> 
              </div>
              CATI Services
            </h2>
            <p className="text-slate-200 text-base md:text-lg mb-6 font-light leading-relaxed">We offer a state-of-the-art CATI (Computer Assisted Telephone Interviewing) facility, ideal for short-turnaround surveys such as:</p>
            <div className="border-l-4 border-[#E31837] pl-5 py-4 bg-white/5 rounded-r-xl border-y border-r border-white/10 shadow-sm">
               <ul className="space-y-2 text-slate-300 font-medium text-sm md:text-base">
                 <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#00A651] rounded-full mr-3 shadow-sm"></div> Public Opinion Surveys</li>
                 <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#00A651] rounded-full mr-3 shadow-sm"></div> Customer Satisfaction Surveys</li>
                 <li className="flex items-center"><div className="w-1.5 h-1.5 bg-[#00A651] rounded-full mr-3 shadow-sm"></div> Employee Feedback Surveys</li>
               </ul>
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  </div>
);

const QualityWorkPage = () => (
  <div className="pb-12 pt-8 md:pt-10 border-t border-white/40 bg-white/70 backdrop-blur-md">
    <div className="py-8 md:py-10 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
        <Reveal><h1 className="text-4xl md:text-5xl font-black mb-3 tracking-tight text-slate-900">Quality & <span className={theme.gradientText}>Clientele</span></h1><p className="text-base md:text-xl text-slate-700 font-medium max-w-3xl mx-auto">Our commitment to excellence and the partners who trust us.</p></Reveal>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-12 space-y-12">
      <Reveal>
        <div className={`p-8 md:p-12 bg-slate-50/80 border border-slate-200/60 shadow-sm rounded-3xl text-center max-w-4xl mx-auto relative overflow-hidden border-t-4 border-t-[#00A651] group`}>
          <div className="w-14 h-14 md:w-16 md:h-16 bg-green-100/80 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-green-200/60 shadow-sm transform rotate-3 relative z-10 hover:rotate-0 transition-transform duration-500">
            <ShieldCheck className="w-7 h-7 md:w-8 md:h-8 text-[#00A651]" />
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-4 tracking-tight relative z-10">Our Approach / Quality Commitment</h2>
          <p className="text-base text-slate-600 font-medium italic leading-relaxed mb-4 relative z-10">"Consumer research is a 'garbage in, garbage out' business - a strong culture of quality is essential."</p>
          <p className="text-sm md:text-base text-slate-800 font-semibold leading-relaxed max-w-3xl mx-auto relative z-10">We believe that senior team involvement is critical to achieving high-quality project outcomes. Their knowledge, commitment, and accessibility benefit both their teams and our clients.</p>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        {[
          { title: "Multi-Tiered Training and Control System", items: ["Classroom briefings", "Mock calls and accompanied field trials", "Spot checks and back checks at the interviewer and supervisor levels"], icon: <Database />, colorClass: "text-[#E31837]", bgClass: "bg-red-50/80", borderClass: "border-red-100", extra: "These procedures are formalised in our Quality Policy, supported by an independent audit team." },
          { title: "Quality Control Department", items: ["Given the importance of quality, our QC department is headed by a director who ensures rigorous standards across every project."], icon: <Target />, colorClass: "text-[#00A651]", bgClass: "bg-green-50/80", borderClass: "border-green-100" },
          { title: "Senior Team Involvement", items: ["Experienced", "Passionate and committed", "Grounded in integrity", "Positive and approachable"], icon: <Users />, colorClass: "text-[#2E3192]", bgClass: "bg-blue-50/80", borderClass: "border-blue-100", extra: "We involve senior team members in every study - because that is the only way to ensure consistently high-quality work. Our senior leaders are:" }
        ].map((block, idx) => (
          <Reveal key={idx} delay={idx * 150} direction="up" type="zoom" className="h-full">
            <div className={`p-8 ${theme.bentoCard} h-full flex flex-col`}>
              <SectionHeader icon={block.icon} title={block.title} colorClass={block.colorClass} bgClass={block.bgClass} />
              {block.extra && block.title === "Senior Team Involvement" && <p className="text-slate-800 font-semibold mb-4 text-sm">{block.extra}</p>}
              <ul className="space-y-3 text-slate-700 font-medium text-sm flex-grow">
                {block.items.map((item, i) => <li key={i} className="flex items-start"><CheckCircle2 className={`w-4 h-4 mr-2.5 flex-shrink-0 mt-0.5 ${block.colorClass}`} /> <span className="leading-snug">{item}</span></li>)}
              </ul>
              {block.extra && block.title !== "Senior Team Involvement" && <div className="mt-6 p-4 bg-slate-50/80 rounded-xl border border-slate-200/60 text-sm text-slate-700 font-medium italic shadow-sm">{block.extra}</div>}
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className={`p-8 md:p-12 ${theme.bentoCard} relative overflow-hidden border-t-4 border-[#2E3192]`}>
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-10 md:mb-12 text-center tracking-tight">Our Work / Clientele</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 relative z-10 mb-12 md:mb-16">
            <div className="flex flex-col justify-center">
              <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-4 tracking-tight">6000+ Projects Delivered Since Inception</h3>
              <p className="text-slate-700 font-medium leading-relaxed mb-8 text-base md:text-lg">We have had the privilege of partnering with some of India's and the world's most respected brands. Our core clientele accounts for over 90% of our revenue.</p>
              
              <div>
                <h4 className="text-slate-900 font-bold mb-4 uppercase tracking-widest text-sm">We also work with:</h4>
                <div className="flex flex-wrap gap-2 md:gap-3">
                  {["Mid-sized enterprises", "Startups", "Non-profit organisations", "Government agencies"].map((tag, i) => (
                    <span key={i} className="bg-slate-50/80 border border-slate-200/60 text-slate-800 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="bg-slate-50/80 p-8 md:p-10 rounded-[2rem] border border-slate-200/60 shadow-sm h-full flex flex-col group">
               <h4 className="text-slate-900 font-black mb-6 md:mb-8 text-xl md:text-2xl flex items-center"><Activity className="w-6 h-6 md:w-8 md:h-8 mr-3 text-[#00A651]"/> Industries We Serve</h4>
               <p className="text-slate-700 text-sm md:text-base font-medium mb-6 md:mb-8">Our experience spans a wide range of industries and brand lifecycles:</p>
               <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6 flex-grow">
                 {INDUSTRIES.map((ind, i) => (
                   <li key={i} className="flex items-start text-slate-800 font-semibold text-sm md:text-base">
                     <div className="w-3 md:w-4 h-[2px] bg-gradient-to-r from-[#E31837] to-transparent mr-3 mt-2.5 rounded-full flex-shrink-0"></div> 
                     <span className="leading-snug">{ind}</span>
                   </li>
                 ))}
               </ul>
            </div>
          </div>

          {/* Bottom Section: Full Width Logos */}
          <div className="space-y-8 md:space-y-12 relative z-10 border-t border-slate-200/50 pt-10 md:pt-16">
            <div className="bg-slate-50/80 border border-slate-200/60 rounded-2xl md:rounded-[2rem] p-8 md:p-12 shadow-sm text-center transition-all hover:shadow-md">
               <p className="text-slate-600 uppercase tracking-widest text-sm md:text-base font-bold mb-6 md:mb-10">Our Key Clients</p>
               <img src="/Key_Client.png" alt="Key Clients" className="w-full max-w-5xl mx-auto h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500" />
            </div>
            <div className="bg-slate-50/80 border border-slate-200/60 rounded-2xl md:rounded-[2rem] p-8 md:p-12 shadow-sm text-center transition-all hover:shadow-md">
               <p className="text-slate-600 uppercase tracking-widest text-sm md:text-base font-bold mb-6 md:mb-10">Extended Client List</p>
               <img src="/Long_Client_List.png" alt="Extended Client List" className="w-full max-w-5xl mx-auto h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500" />
            </div>
          </div>

        </div>
      </Reveal>
    </div>
  </div>
);

const CultureCareerPage = ({ setIsModalOpen }) => {
  return (
    <div className="pb-12 relative pt-8 md:pt-10 border-t border-white/40 bg-slate-50/70 backdrop-blur-md">
      <div className="py-8 md:py-10 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
          <Reveal><h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tight text-slate-900">Life at Modulus & <span className={theme.gradientText}>Career</span></h1></Reveal>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-12 md:space-y-16 pb-12">
        
        {/* LIFE AT MODULUS */}
        <Reveal>
          <div className="text-center mb-10"><h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Life at Modulus</h2></div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            <div className={`p-8 md:p-10 ${theme.bentoCard} relative overflow-hidden group border-l-4 border-l-[#E31837]`}>
              <SectionHeader icon={<Smile />} title="Work Hard, Party Harder" colorClass="text-[#E31837]" bgClass="bg-red-50/80" />
              <p className="text-slate-700 font-medium leading-relaxed text-sm md:text-base relative z-10">At Modulus, we believe in working hard and celebrating often. Informal get-togethers are a regular part of our culture. Our founders believe in working with a smile - humour runs through our workspaces, keeping us energised through the long hours typical of market research.</p>
            </div>
            <div className={`p-8 md:p-10 ${theme.bentoCard} relative overflow-hidden group border-l-4 border-l-[#00A651]`}>
              <SectionHeader icon={<Heart />} title="Contribution to a Social Cause" colorClass="text-[#00A651]" bgClass="bg-green-50/80" />
              <p className="text-slate-700 font-medium leading-relaxed text-sm md:text-base relative z-10">Every Foundation Day, a contribution is made to NGOs that work with underprivileged citizens. Giving back to the community is an integral part of who we are.</p>
            </div>
          </div>
          
          <div className={`mt-8 md:mt-10 p-8 md:p-12 ${theme.bentoCard} relative overflow-hidden border-t-4 border-[#2E3192]`}>
             <div className="relative z-10">
               <SectionHeader icon={<Calendar />} title="Celebrations & Events" colorClass="text-[#2E3192]" bgClass="bg-blue-50/80" />
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                 {[
                   { t: "Foundation Day", d: "We celebrate our Foundation Day on April 6 every year across all offices." },
                   { t: "Saraswati Puja", d: "In line with the \"Mode\" legacy, Saraswati Puja and Diwali are celebrated in all our offices across the country." },
                   { t: "Women's Day", d: "We celebrate Women's Day with enthusiasm across all locations, recognising achievements without distinction." },
                   { t: "Get-Togethers", d: "Regular evenings of fun and camaraderie help strengthen bonds across teams." },
                   { t: "Picnic Time", d: "Our annual picnics are a highlight, offering a chance to relax and connect outside of work." }
                 ].map((ev, i) => (
                   <div key={i} className="bg-slate-50/80 p-6 rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow">
                     <h4 className="text-slate-900 font-bold mb-2 text-base">{ev.t}</h4>
                     <p className="text-slate-700 text-sm font-medium leading-relaxed">{ev.d}</p>
                   </div>
                 ))}
               </div>
             </div>
          </div>
        </Reveal>

        {/* CAREER */}
        <Reveal>
          <div className="text-center mb-10"><h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Career</h2></div>
          <div className={`p-8 md:p-12 ${theme.bentoCard} mb-12 relative overflow-hidden`}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 relative z-10">
              <div>
                <SectionHeader icon={<Users />} title="Our Philosophy" colorClass="text-[#E31837]" bgClass="bg-red-50/80" />
                <p className="text-slate-800 font-semibold leading-relaxed mb-4 md:mb-6 text-sm md:text-base">Modulus is an equal opportunity employer that champions a performance-driven culture. We offer careers, not just jobs. Many of our leaders, including our Managing Director and Deputy Managing Director, began their journeys as interviewers and grew into leadership roles.</p>
                <p className="text-slate-800 font-semibold leading-relaxed text-sm md:text-base">We encourage our people to think, explore new ideas, and venture into the unknown. We see ourselves as partners in building on your strengths and bridging skill, knowledge, and behavioural gaps.</p>
              </div>
              <div>
                <SectionHeader icon={<HeartHandshake />} title="Trust and Teamwork" colorClass="text-[#00A651]" bgClass="bg-green-50/80" />
                <p className="text-slate-800 font-semibold leading-relaxed mb-4 md:mb-6 text-sm md:text-base">Trust is central to both our business and our people philosophy. We believe in our people and want them to trust us fully. While we celebrate individual excellence, we know that organisations thrive on successful teamwork. That is why we value connection across individuals and functions. We don't believe in silos - and we don't want our people to work in them.</p>
                <p className="text-slate-800 font-semibold leading-relaxed text-sm md:text-base">Modulus has a <strong className="text-slate-900 font-black">flat organisational structure</strong>, free from unnecessary bureaucracy. Cross-functional teams are common during project implementation, and we use these as learning platforms for future leaders.</p>
              </div>
            </div>
            
            <div className="mt-10 md:mt-12 text-center p-8 md:p-10 bg-slate-50/80 rounded-[2rem] border border-slate-200/60 shadow-sm relative z-10">
              <p className="text-xl md:text-2xl font-black text-slate-900 italic mb-3 drop-shadow-sm">"If you are confident in your abilities, the sky is truly the limit at Modulus."</p>
              <p className="text-slate-700 text-sm md:text-base font-medium max-w-3xl mx-auto">Our leadership team brings together veteran experience and youthful energy, bound by a strong value system and professionalism.</p>
            </div>
          </div>
        </Reveal>

        {/* OPEN OPPORTUNITIES */}
        <Reveal>
          <div className="mb-16">
            <h3 className="text-3xl md:text-4xl font-black text-slate-900 mb-10 tracking-tight text-center">Open Opportunities</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 max-w-5xl mx-auto">
              <div className={`p-6 md:p-8 ${theme.bentoCard} text-center group hover:border-[#FF3B3B]/50`}>
                <div className="w-14 h-14 bg-red-50/80 rounded-xl flex items-center justify-center mx-auto mb-4 border border-red-100 shadow-sm group-hover:scale-110 transition-transform">
                  <Target className="w-7 h-7 text-[#E31837]" />
                </div>
                <strong className="text-slate-900 text-lg md:text-xl font-black block mb-2 tracking-tight">Research Division</strong>
                <span className="text-slate-700 text-xs md:text-sm font-medium leading-relaxed">Based out of Delhi and Kolkata, with talented and experienced researchers working under senior leadership.</span>
              </div>
              <div className={`p-6 md:p-8 ${theme.bentoCard} text-center group hover:border-[#00E676]/50`}>
                <div className="w-14 h-14 bg-green-50/80 rounded-xl flex items-center justify-center mx-auto mb-4 border border-green-100 shadow-sm group-hover:scale-110 transition-transform">
                  <BarChart3 className="w-7 h-7 text-[#00A651]" />
                </div>
                <strong className="text-slate-900 text-lg md:text-xl font-black block mb-2 tracking-tight">Analysis Division</strong>
                <span className="text-slate-700 text-xs md:text-sm font-medium leading-relaxed">Based in Kolkata, comprises a talented group of data experts.</span>
              </div>
              <div className={`p-6 md:p-8 ${theme.bentoCard} text-center group hover:border-[#4D4DFF]/50`}>
                <div className="w-14 h-14 bg-blue-50/80 rounded-xl flex items-center justify-center mx-auto mb-4 border border-blue-100 shadow-sm group-hover:scale-110 transition-transform">
                  <Briefcase className="w-7 h-7 text-[#4D4DFF]" />
                </div>
                <strong className="text-slate-900 text-lg md:text-xl font-black block mb-2 tracking-tight">Project Management</strong>
                <span className="text-slate-700 text-xs md:text-sm font-medium leading-relaxed">Based in Delhi and Kolkata and comprise passionate and capable professionals.</span>
              </div>
            </div>
            
            <h4 className="text-xl md:text-2xl font-black text-slate-900 mb-6 md:mb-8 border-l-4 border-[#4D4DFF] pl-4">Current Openings</h4>
            <div className="space-y-8 md:space-y-10">
              {JOBS.map((job, idx) => (
                <div key={idx} className={`p-8 md:p-12 ${theme.bentoCard} relative overflow-hidden group hover:border-[#4D4DFF]/40`}>
                  <div className="flex flex-col lg:flex-row justify-between border-b border-slate-200/60 pb-6 mb-6">
                    <div>
                      <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-4">{job.title}</h3>
                      <div className="flex flex-wrap gap-3 text-[10px] md:text-xs font-bold uppercase tracking-widest">
                        <span className="bg-blue-50/80 text-[#2E3192] px-3 py-1.5 rounded-lg border border-blue-200/60 shadow-sm">Level: {job.level}</span>
                        <span className="bg-slate-50/80 text-slate-800 px-3 py-1.5 rounded-lg border border-slate-200/60 shadow-sm">{job.division}</span>
                        <span className="bg-green-50/80 text-[#00A651] px-3 py-1.5 rounded-lg border border-green-200/60 shadow-sm">{job.location}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <h4 className="text-slate-600 font-bold uppercase tracking-widest mb-3 flex items-center text-xs md:text-sm"><Info className="w-4 h-4 mr-2"/> Job Brief</h4>
                    <p className="text-slate-700 text-sm md:text-base font-medium leading-relaxed">{job.brief}</p>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
                    <div>
                      <h4 className="font-bold text-slate-900 mb-4 md:mb-5 flex items-center text-base md:text-lg"><Target className="w-4 h-4 md:w-5 md:h-5 mr-2.5 text-[#E31837] drop-shadow-[0_0_5px_rgba(227,24,55,0.3)]"/> Responsibilities / Duties</h4>
                      <ul className="space-y-3 text-slate-700 font-medium text-sm md:text-base">
                        {job.duties.map((duty, i) => {
                          if (duty === duty.toUpperCase() && duty.length > 5) {
                            return <li key={i} className="font-bold text-slate-900 mt-6 mb-2 border-b border-slate-200/60 pb-1 uppercase tracking-widest text-xs">{duty}</li>
                          }
                          return (
                            <li key={i} className="flex items-start">
                              <div className="w-1.5 h-1.5 bg-[#E31837] rounded-full mr-2.5 mt-2 flex-shrink-0 shadow-sm"></div> 
                              <span className="leading-snug">{duty}</span>
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                    <div>
                      <div className="mb-8">
                        <h4 className="font-bold text-slate-900 mb-4 md:mb-5 flex items-center text-base md:text-lg"><CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 mr-2.5 text-[#00A651] drop-shadow-[0_0_5px_rgba(0,166,81,0.3)]"/> Qualifications & Skills</h4>
                        <ul className="space-y-3 text-slate-700 font-medium text-sm md:text-base">
                          {job.skills.map((skill, i) => <li key={i} className="flex items-start"><div className="w-1.5 h-1.5 bg-[#00A651] rounded-full mr-2.5 mt-2 flex-shrink-0 shadow-sm"></div> <span className="leading-snug">{skill}</span></li>)}
                        </ul>
                      </div>
                      
                      {job.attributes.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-bold text-slate-900 mb-3 text-sm">Key Attributes</h4>
                          <div className="flex flex-wrap gap-2">
                            {job.attributes.map((attr, i) => <span key={i} className="bg-slate-50/80 border border-slate-200/60 text-slate-800 px-3 py-1.5 rounded-lg text-xs font-semibold shadow-sm">{attr}</span>)}
                          </div>
                        </div>
                      )}
                      
                      {job.interactions && job.interactions.length > 0 && (
                        <div>
                          <h4 className="font-bold text-slate-900 mb-3 text-sm">Interactions</h4>
                          <ul className="text-slate-700 font-medium text-xs md:text-sm space-y-2">
                            {job.interactions.map((int, i) => <li key={i} className="bg-slate-50/80 px-3 py-2 rounded-lg border border-slate-200/60 shadow-sm">{int}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* RESUME DROP ZONE */}
        <Reveal>
          <div className="mt-16 md:mt-24 text-center bg-white/70 backdrop-blur-xl border border-slate-200/50 p-12 md:p-16 rounded-[2.5rem] shadow-md relative overflow-hidden">
            <h3 className="text-2xl md:text-4xl font-black text-slate-900 mb-4 relative z-10">Apply Now</h3>
            <p className="text-slate-700 text-sm md:text-base font-medium mb-8 max-w-2xl mx-auto leading-relaxed relative z-10">Interested candidates are invited to send their resume to: <br/><strong className="text-slate-900 font-bold block mt-2 text-lg md:text-xl tracking-wide">contact@modulus-research.com</strong></p>
            <div className="flex items-center justify-center space-x-4 mb-8 relative z-10">
              <div className="h-px w-12 bg-slate-300"></div>
              <p className="text-slate-500 uppercase tracking-widest font-bold text-[10px]">OR SECURELY UPLOAD BELOW</p>
              <div className="h-px w-12 bg-slate-300"></div>
            </div>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="bg-[#2E3192] text-white px-8 py-4 rounded-xl font-bold text-sm md:text-base hover:bg-[#1a1c5b] transition-all duration-300 flex items-center mx-auto shadow-md relative z-10 w-full sm:w-auto justify-center"
            >
              <UploadCloud className="w-5 h-5 mr-3 text-white" /> Drop Your Resume Here
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

const ContactPage = () => (
  <div className="pb-12 pt-8 md:pt-10 border-t border-white/40 bg-white/70 backdrop-blur-md">
    <div className="py-8 md:py-10 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
        <Reveal><h1 className="text-4xl md:text-5xl font-black mb-2 tracking-tight text-slate-900">Contact <span className={theme.gradientText}>Us</span></h1></Reveal>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-24">
        <Reveal direction="right" className="h-full">
          <div className="bg-gradient-to-br from-[#2E3192] to-[#1a1c5b] text-white p-8 md:p-12 rounded-[2rem] md:rounded-[2.5rem] shadow-lg h-full relative overflow-hidden border border-blue-900">
            <h2 className="text-2xl md:text-3xl font-black mb-8 relative z-10 tracking-tight">Get In Touch</h2>
            <div className="space-y-6 md:space-y-8 relative z-10">
              <div className="flex items-center group select-none">
                <div className="bg-white/10 p-3 md:p-4 rounded-xl mr-4 md:mr-5 backdrop-blur-sm border border-white/20 shadow-sm group-hover:scale-110 transition-transform"><Phone className="w-5 h-5 md:w-6 md:h-6 text-green-400" /></div>
                <div><p className="text-blue-200 text-[10px] md:text-xs font-bold uppercase tracking-widest mb-1">Call Us</p><p className="text-lg md:text-xl font-medium tracking-wide">+91 9831380980</p></div>
              </div>
              <div className="flex items-center group select-none">
                <div className="bg-white/10 p-3 md:p-4 rounded-xl mr-4 md:mr-5 backdrop-blur-sm border border-white/20 shadow-sm group-hover:scale-110 transition-transform"><Mail className="w-5 h-5 md:w-6 md:h-6 text-red-400" /></div>
                <div><p className="text-blue-200 text-[10px] md:text-xs font-bold uppercase tracking-widest mb-1">Email Us</p><p className="text-sm md:text-base font-medium break-all">contact@modulus-research.com</p></div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal direction="left" className="h-full">
          <div className={`p-8 md:p-12 ${theme.bentoCard} h-full bg-slate-50/80`}>
            <h2 className="text-xl md:text-2xl font-black text-slate-900 mb-4 tracking-tight">Send us a Message</h2>
            <p className="text-slate-600 mb-6 font-medium italic text-xs md:text-sm">{"[Contact form fields: Name, Email, Phone, Subject, Message]"}</p>
            <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" className="w-full px-4 py-3 bg-white/80 border border-slate-200/50 rounded-lg outline-none focus:border-[#2E3192] transition-all font-medium text-slate-900 text-sm shadow-sm" placeholder="Name" />
                <input type="text" className="w-full px-4 py-3 bg-white/80 border border-slate-200/50 rounded-lg outline-none focus:border-[#2E3192] transition-all font-medium text-slate-900 text-sm shadow-sm" placeholder="Subject" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="email" className="w-full px-4 py-3 bg-white/80 border border-slate-200/50 rounded-lg outline-none focus:border-[#2E3192] transition-all font-medium text-slate-900 text-sm shadow-sm" placeholder="Email Address" />
                <input type="tel" className="w-full px-4 py-3 bg-white/80 border border-slate-200/50 rounded-lg outline-none focus:border-[#2E3192] transition-all font-medium text-slate-900 text-sm shadow-sm" placeholder="Phone Number" />
              </div>
              <textarea rows="4" className="w-full px-4 py-3 bg-white/80 border border-slate-200/50 rounded-lg outline-none focus:border-[#2E3192] transition-all resize-none font-medium text-slate-900 text-sm shadow-sm" placeholder="Message"></textarea>
              <button type="submit" className={`w-full bg-[#2E3192] text-white font-bold py-3.5 rounded-lg hover:bg-blue-900 transition-all shadow-md text-sm tracking-wide`}>Send Message</button>
            </form>
          </div>
        </Reveal>
      </div>

      <Reveal>
        <div className="mb-8 md:mb-10 text-center">
          <h2 className="text-2xl md:text-3xl font-black text-slate-900 mb-2 tracking-tight">Our Offices</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {OFFICES.map((office, idx) => (
            <Reveal key={idx} delay={(idx % 4) * 50} type="zoom" className="h-full">
              <div className={`p-6 bg-white/80 border border-slate-200/50 shadow-sm rounded-2xl h-full flex flex-col hover:-translate-y-1 hover:shadow-md hover:border-[#2E3192] transition-all duration-300 relative overflow-hidden group backdrop-blur-md`}>
                {/* Decorative expanding circle */}
                <div className="absolute -top-12 -right-12 w-24 h-24 bg-slate-50/50 rounded-full transition-transform duration-500 group-hover:scale-[2.5] z-0 pointer-events-none"></div>
                
                <div className="flex justify-between items-start mb-5 relative z-10">
                  <div className="w-12 h-12 rounded-xl bg-blue-50/80 text-[#2E3192] flex items-center justify-center border border-blue-100/50 shadow-sm group-hover:bg-[#2E3192] group-hover:text-white transition-colors duration-300">
                    <MapPin className="w-6 h-6" />
                  </div>
                  {office.city === 'Kolkata' && (
                      <span className="bg-red-50/80 text-[#E31837] border border-red-100/50 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest flex items-center shadow-sm">
                        <Award className="w-3 h-3 mr-1.5" /> HQ
                      </span>
                  )}
                </div>

                <div className="relative z-10 flex flex-col flex-grow">
                  <h3 className="text-xl font-black text-slate-900 mb-1.5 tracking-tight group-hover:text-[#2E3192] transition-colors">{office.city}</h3>
                  <p className="text-[10px] text-[#00A651] font-bold uppercase tracking-widest mb-3">{office.type}</p>
                  <p className="text-sm text-slate-600 font-medium leading-relaxed flex-grow">{office.address}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Reveal>
    </div>
  </div>
);

const PrivacyPolicyPage = () => (
  <div className="pb-12 bg-white/80 backdrop-blur-xl">
    <div className="py-8 md:py-12 relative overflow-hidden border-b border-white/40 bg-slate-50/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
        <Reveal><h1 className="text-3xl md:text-4xl font-black mb-2 tracking-tight text-slate-900">Privacy <span className={theme.gradientText}>Policy</span></h1></Reveal>
      </div>
    </div>
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 md:py-16">
      <Reveal>
        <div className={`p-8 md:p-12 ${theme.bentoCard}`}>
          <div className="space-y-4 md:space-y-6 text-slate-700 font-medium leading-relaxed text-sm md:text-base">
            <p><strong>Effective Date:</strong> March 2026</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">1. Introduction</h3>
            <p>Modulus Research & Analysis Pvt. Ltd. ("Modulus", "we", "our", or "us") is committed to protecting the privacy and security of the personal data we collect. As a premier market research agency, confidentiality and data integrity are at the core of our operations.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">2. Information We Collect</h3>
            <p>We collect information primarily for market research purposes. This may include demographic data, opinions, purchasing habits, and audio/video recordings during consumer immersions or CATI/CAPI surveys. We also collect basic contact information when you use our website's contact forms or submit resumes.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">3. How We Use Your Information</h3>
            <p>Research data is aggregated, anonymised, and analysed to identify consumer trends and insights for our clients. We strictly ensure that personally identifiable information (PII) is never sold, traded, or shared with third parties for marketing purposes without explicit consent.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">4. Data Security</h3>
            <p>We employ industry-standard, ISO-compliant security protocols, including secure server hosting, encryption, and strict access controls across our 16 regional offices, to safeguard your data against unauthorised access or loss.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">5. Your Rights</h3>
            <p>Depending on your jurisdiction, you retain the right to access, correct, or request the deletion of any personal data you have shared with us.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">6. Contact Us</h3>
            <p>If you have any questions about this Privacy Policy or our data practices, please contact us at: <strong className="text-slate-900 font-bold">contact@modulus-research.com</strong></p>
          </div>
        </div>
      </Reveal>
    </div>
  </div>
);

const TermsOfServicePage = () => (
  <div className="pb-12 bg-white/80 backdrop-blur-xl">
    <div className="py-8 md:py-12 relative overflow-hidden border-b border-white/40 bg-slate-50/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
        <Reveal><h1 className="text-3xl md:text-4xl font-black mb-2 tracking-tight text-slate-900">Terms of <span className={theme.gradientText}>Service</span></h1></Reveal>
      </div>
    </div>
    <div className="max-w-4xl mx-auto px-6 lg:px-8 py-12 md:py-16">
      <Reveal>
        <div className={`p-8 md:p-12 ${theme.bentoCard}`}>
          <div className="space-y-4 md:space-y-6 text-slate-700 font-medium leading-relaxed text-sm md:text-base">
            <p><strong>Effective Date:</strong> March 2026</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">1. Acceptance of Terms</h3>
            <p>By accessing and using the website of Modulus Research & Analysis Pvt. Ltd., you accept and agree to be bound by the terms and provision of this agreement.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">2. Description of Services</h3>
            <p>Modulus provides end-to-end primary market research, data collection, and analytics services. The information provided on this website is for general informational purposes regarding these services.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">3. Intellectual Property Rights</h3>
            <p>All content, logos, graphics, and methodologies displayed on this site are the intellectual property of Modulus or our respective clients. Unauthorised reproduction, distribution, or use of these materials is strictly prohibited.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">4. Limitation of Liability</h3>
            <p>While we strive for the highest data integrity, the insights and research findings are provided "as is". Modulus shall not be liable for any direct, indirect, incidental, or consequential business losses resulting from the use or inability to use our research deliverables.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">5. Client Confidentiality</h3>
            <p>We adhere to strict Non-Disclosure Agreements (NDAs) with all our clients. Client identities and proprietary research parameters are protected at all times.</p>
            <h3 className="text-base md:text-lg font-bold text-slate-900 mt-6 md:mt-8 mb-2">6. Governing Law</h3>
            <p>These terms shall be governed by and construed in accordance with the laws of India, and any disputes relating to these terms will be subject to the exclusive jurisdiction of the courts of Kolkata, India.</p>
          </div>
        </div>
      </Reveal>
    </div>
  </div>
);

// --- MAIN APP COMPONENT ---
export default function App() {
  const [currentPage, setCurrentPage] = useState('Main');
  const [activeSection, setActiveSection] = useState('Home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleResumeSubmit = (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => { setIsModalOpen(false); setIsSubmitted(false); }, 3000);
  };

  // Smooth scroll logic
  const navigateTo = (pageId) => {
    setMobileMenuOpen(false);

    if (pageId === 'PrivacyPolicy' || pageId === 'TermsOfService') {
      setCurrentPage(pageId);
      window.scrollTo(0, 0);
      return;
    }

    if (currentPage !== 'Main') {
      setCurrentPage('Main');
      setTimeout(() => {
        scrollToSection(pageId);
      }, 100);
    } else {
      scrollToSection(pageId);
    }
  };

  const scrollToSection = (pageId) => {
    const element = document.getElementById(pageId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);

      // Scroll Spy Logic
      if (currentPage === 'Main') {
        const sections = ['Home', 'About', 'Services', 'QualityWork', 'CultureCareer', 'Contact'];
        let current = 'Home';
        for (const id of sections) {
          const section = document.getElementById(id);
          if (section) {
            const rect = section.getBoundingClientRect();
            if (rect.top <= window.innerHeight / 3) {
              current = id;
            }
          }
        }
        setActiveSection(current);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [currentPage]);

  const navItems = [
    { id: 'Home', label: 'Home' }, { id: 'About', label: 'About Us' }, { id: 'Services', label: 'Services' },
    { id: 'QualityWork', label: 'Quality & Work' }, { id: 'CultureCareer', label: 'Life at Modulus & Career' }, { id: 'Contact', label: 'Contact Us' }
  ];

  return (
    <div className="min-h-screen flex flex-col font-sans text-slate-700 bg-transparent selection:bg-[#00A651] selection:text-white relative z-0">

      {/* Global Brand Background */}
      <SiteBackground />

      <style dangerouslySetInnerHTML={{__html: `
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-20px) scale(1.02); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(15px) scale(0.98); }
        }
        @keyframes float-fast {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          50% { transform: translate(-15px, 10px) scale(1.02); }
        }
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-float-slow { animation: float-slow 8s ease-in-out infinite; }
        .animate-float-medium { animation: float-medium 6s ease-in-out infinite; }
        .animate-float-fast { animation: float-fast 5s ease-in-out infinite; }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        .animate-scale-in { animation: scale-in 0.3s ease-out forwards; }
      `}} />

      {/* Floating Light Glass Navbar */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'top-0 py-2 md:py-3' : 'top-0 py-4 md:py-6'}`}>
        <div className={`mx-auto px-4 sm:px-6 transition-all duration-500 max-w-7xl`}>
          <div className={`flex justify-between items-center transition-all duration-500 ${scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm border border-slate-200/50 rounded-full px-4 md:px-6 py-2.5' : 'bg-transparent'}`}>
            
            <div className="flex items-center cursor-pointer ml-1 md:ml-2" onClick={() => navigateTo('Home')}>
              <div className="flex items-center">
                <img src="/logo.png" alt="Modulus Logo" className="h-8 md:h-10 w-auto drop-shadow-sm transition-transform duration-300 hover:scale-105 object-contain" />
              </div>
            </div>

            <div className="hidden lg:flex items-center space-x-1 bg-white/60 px-2 py-1.5 rounded-full shadow-sm border border-slate-200/50 backdrop-blur-md">
              {navItems.map((item) => (
                <button 
                  key={item.id} 
                  onClick={() => navigateTo(item.id)} 
                  className={`px-4 py-2 rounded-full text-xs md:text-sm font-bold tracking-wide transition-all duration-300 ${currentPage === 'Main' && activeSection === item.id ? 'text-white bg-[#2E3192] shadow-sm' : 'text-slate-700 hover:text-[#2E3192] hover:bg-white/80'}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-slate-800 p-2 mr-1 bg-white border border-slate-200/50 rounded-full shadow-sm backdrop-blur-md">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200/50 shadow-xl absolute w-full left-0 top-full z-40 px-6 py-6 md:py-8 space-y-2 origin-top animate-fade-in">
            {navItems.map((item) => (
              <button 
                key={item.id} 
                onClick={() => navigateTo(item.id)} 
                className={`block w-full text-left px-5 py-3 md:py-4 rounded-xl text-sm md:text-base font-bold uppercase tracking-widest transition-colors ${currentPage === 'Main' && activeSection === item.id ? 'text-[#2E3192] bg-blue-50 border border-blue-100' : 'text-slate-700 hover:text-[#2E3192] bg-slate-50/50 border border-transparent'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </nav>

      {/* Main Content (Single Page Architecture) */}
      <main className="flex-grow pt-[80px] md:pt-[100px] z-10">
        {currentPage === 'Main' && (
          <div className="flex flex-col">
            <div id="Home"><HomePage navigateTo={navigateTo} /></div>
            <div id="About"><AboutPage /></div>
            <div id="Services"><ServicesPage /></div>
            <div id="QualityWork"><QualityWorkPage /></div>
            <div id="CultureCareer"><CultureCareerPage setIsModalOpen={setIsModalOpen} /></div>
            <div id="Contact"><ContactPage /></div>
          </div>
        )}
        
        {/* Separate pages for legal stuff to not clutter main scroll */}
        {currentPage === 'PrivacyPolicy' && <div className="pt-[40px] md:pt-[60px]"><PrivacyPolicyPage /></div>}
        {currentPage === 'TermsOfService' && <div className="pt-[40px] md:pt-[60px]"><TermsOfServicePage /></div>}
      </main>

      {/* Dark Theme Corporate Footer (Glass Style) */}
      <footer className="bg-[#0a1f44]/95 backdrop-blur-2xl border-t border-white/10 pt-10 md:pt-14 text-slate-300 relative z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
            
            {/* Main Footer Links Area */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 border-b border-white/10 pb-10 md:pb-14 mb-6 md:mb-8">
                
                {/* Brand & Description */}
                <div className="flex flex-col items-start">
                    <div className="mb-4 md:mb-5 inline-flex items-center justify-center w-12 h-12 md:w-14 md:h-14 rounded-full bg-white shadow-sm border border-white/20 p-0.5">
                        <img src="/Modulus_Footer.png" alt="Modulus" className="w-full h-full object-contain rounded-full" />
                    </div>
                    <h4 className="text-[9px] md:text-[10px] lg:text-[11px] mb-3 uppercase whitespace-nowrap" style={{ fontFamily: 'Arial, Helvetica, sans-serif', fontWeight: 900, letterSpacing: '0.5px' }}>
                      <span className="text-[#E31837]">MODULUS</span> <span className="text-[#00A651]">RESEARCH &amp; ANALYSIS</span> <span className="text-[#4D4DFF]">PVT. LTD.</span>
                    </h4>
                    <p className="text-slate-400 text-[11px] md:text-sm leading-relaxed font-light">
                      An Experienced, Dependable Market Research Partner for Your Critical Business Decisions. Empowering brands with uncompromising primary research data across India and beyond.
                    </p>
                </div>

                {/* Quick Links */}
                <div>
                    <h4 className="text-white font-bold uppercase tracking-widest text-[10px] md:text-xs mb-5 md:mb-6 flex items-center">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#4D4DFF] rounded-full mr-2 md:mr-3 shadow-[0_0_5px_#4D4DFF]"></div> Quick Links
                    </h4>
                    <ul className="space-y-3 md:space-y-4 text-slate-300 text-sm font-light">
                        <li><button onClick={() => navigateTo('Home')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> Home</button></li>
                        <li><button onClick={() => navigateTo('About')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> About Us</button></li>
                        <li><button onClick={() => navigateTo('QualityWork')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> Quality & Clientele</button></li>
                        <li><button onClick={() => navigateTo('CultureCareer')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> Life at Modulus & Career</button></li>
                        <li><button onClick={() => navigateTo('Contact')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> Contact Us</button></li>
                    </ul>
                </div>

                {/* Our Services */}
                <div>
                    <h4 className="text-white font-bold uppercase tracking-widest text-[10px] md:text-xs mb-5 md:mb-6 flex items-center">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#4D4DFF] rounded-full mr-2 md:mr-3 shadow-[0_0_5px_#4D4DFF]"></div> Our Services
                    </h4>
                    <ul className="space-y-3 md:space-y-4 text-slate-300 text-sm font-light">
                        <li><button onClick={() => navigateTo('Services')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> End-to-End Research</button></li>
                        <li><button onClick={() => navigateTo('Services')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> Data Collection</button></li>
                        <li><button onClick={() => navigateTo('Services')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> Consumer Immersions</button></li>
                        <li><button onClick={() => navigateTo('Services')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> Analytics & Processing</button></li>
                        <li><button onClick={() => navigateTo('Services')} className="hover:text-white transition-colors text-left flex items-center group"><ChevronRight className="w-3 h-3 mr-2 text-slate-500 group-hover:text-[#4D4DFF] transition-colors"/> CATI Services</button></li>
                    </ul>
                </div>

                {/* Contact Info */}
                <div>
                    <h4 className="text-white font-bold uppercase tracking-widest text-[10px] md:text-xs mb-5 md:mb-6 flex items-center">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-[#4D4DFF] rounded-full mr-2 md:mr-3 shadow-[0_0_5px_#4D4DFF]"></div> Contact Info
                    </h4>
                    <ul className="space-y-4 md:space-y-5 text-slate-300 text-sm font-light">
                        <li className="flex items-start group select-none">
                            <Mail className="w-4 h-4 md:w-5 md:h-5 mr-3 md:mr-4 text-[#FF3B3B] shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                            <a href="mailto:contact@modulus-research.com" className="hover:text-white transition-colors break-all cursor-default">contact@modulus-research.com</a>
                        </li>
                        <li className="flex items-start group select-none">
                            <Phone className="w-4 h-4 md:w-5 md:h-5 mr-3 md:mr-4 text-[#00E676] shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                            <a href="tel:+919831380980" className="hover:text-white transition-colors cursor-default">+91 9831380980</a>
                        </li>
                        <li className="flex items-start group">
                            <MapPin className="w-4 h-4 md:w-5 md:h-5 mr-3 md:mr-4 text-[#4D4DFF] shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                            <span className="leading-relaxed">44, Shantipally, Sanjay Bhawan, 5th Floor, Kolkata-700107</span>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Bottom Bar (Copyright, Legal, Socials perfectly aligned to the right) */}
            <div className="flex flex-col lg:flex-row justify-between items-center text-xs md:text-sm font-light text-slate-500 pb-8 md:pb-12 gap-6 w-full">
                
                {/* 1. Copyright */}
                <div className="text-center lg:text-left text-slate-400 w-full lg:w-auto">
                    &copy; {new Date().getFullYear()} Modulus Research & Analysis Pvt. Ltd. All rights reserved.
                </div>
                
                {/* 2. Socials & Legal aligned perfectly to the right */}
                <div className="flex flex-wrap justify-center items-center gap-4 md:gap-6 w-full lg:w-auto">
                    <button onClick={() => navigateTo('PrivacyPolicy')} className="hover:text-white text-slate-400 cursor-pointer transition-colors font-medium uppercase tracking-widest text-[10px] md:text-xs">Privacy Policy</button>
                    <button onClick={() => navigateTo('TermsOfService')} className="hover:text-white text-slate-400 cursor-pointer transition-colors font-medium uppercase tracking-widest text-[10px] md:text-xs">Terms of Service</button>
                    
                    <div className="w-px h-4 md:h-6 bg-white/10 mx-2 hidden sm:block"></div>
                    
                    <a href="https://www.facebook.com/share/1E27HdK8uW/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-[#4D4DFF] hover:border-[#4D4DFF] transition-all duration-300">
                        <span className="font-bold text-sm">f</span>
                    </a>
                    <a href="https://www.linkedin.com/company/modulus-research-analysis/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-[#4D4DFF] hover:border-[#4D4DFF] transition-all duration-300">
                        <span className="font-bold text-sm">in</span>
                    </a>
                </div>

            </div>
        </div>
      </footer>

      {/* Resume Upload Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4 animate-fade-in">
          <div className="bg-white/95 backdrop-blur-2xl w-full max-w-md rounded-2xl shadow-2xl overflow-hidden relative animate-scale-in border border-slate-200/50">
            <button onClick={() => setIsModalOpen(false)} className="absolute top-4 right-4 text-slate-500 hover:text-slate-900 transition-colors bg-slate-100/80 p-2 rounded-full border border-slate-300/50">
              <X className="w-5 h-5" />
            </button>
            
            <div className="p-8 md:p-10">
              {!isSubmitted ? (
                <>
                  <div className="w-12 h-12 bg-blue-50/80 text-[#2E3192] rounded-xl flex items-center justify-center mb-5 border border-blue-100/50 shadow-sm">
                    <UploadCloud className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-2 tracking-tight">Submit Your Resume</h3>
                  <p className="text-slate-600 font-medium mb-6 md:mb-8 text-xs md:text-sm">Upload your CV and we will securely store it in our system for review.</p>
                  
                  <form onSubmit={handleResumeSubmit} className="space-y-4">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 mb-1.5 uppercase tracking-widest">Full Name</label>
                      <input type="text" required className="w-full px-4 py-2.5 md:py-3 bg-slate-50/80 border border-slate-200/50 rounded-lg outline-none focus:border-[#2E3192] text-slate-900 text-sm transition-all shadow-sm" placeholder="John Doe" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 mb-1.5 uppercase tracking-widest">Email Address</label>
                      <input type="email" required className="w-full px-4 py-2.5 md:py-3 bg-slate-50/80 border border-slate-200/50 rounded-lg outline-none focus:border-[#2E3192] text-slate-900 text-sm transition-all shadow-sm" placeholder="john@example.com" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-700 mb-1.5 uppercase tracking-widest">Resume File (PDF, DOCX)</label>
                      <input type="file" required accept=".pdf,.doc,.docx" className="w-full px-4 py-2.5 md:py-3 bg-slate-50/80 border border-slate-200/50 rounded-lg text-xs text-slate-700 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-[10px] file:font-bold file:bg-blue-100/50 file:text-[#2E3192] hover:file:bg-blue-200/50 file:cursor-pointer cursor-pointer transition-all shadow-sm" />
                    </div>
                    <button type="submit" className={`w-full bg-[#2E3192] text-white font-bold py-3 md:py-4 rounded-lg hover:bg-blue-900 transition-all mt-6 text-sm tracking-wide shadow-md`}>
                      Upload to Server
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-10">
                  <div className="w-16 h-16 bg-green-50/80 text-[#00A651] rounded-full flex items-center justify-center mx-auto mb-4 border border-green-100/50 shadow-sm">
                    <FileCheck className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-slate-900 mb-2 tracking-tight">Resume Received!</h3>
                  <p className="text-slate-500 font-light text-sm">Thank you. Your resume has been successfully uploaded to our secure servers.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}